import React, { useState } from 'react';
import { Save, Plus, Minus, AlertTriangle } from 'lucide-react';
import { productService } from '../../../services/productService';

interface StockEditorProps {
  productId: string;
  currentStock: number;
  onUpdateStock: (newStock: number) => Promise<void>;
}

const StockEditor = ({ productId, currentStock, onUpdateStock }: StockEditorProps) => {
  const [stock, setStock] = useState(currentStock);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStockChange = (value: number) => {
    setStock(Math.max(0, value)); // Prevent negative stock
  };

  const handleSubmit = async () => {
    if (stock === currentStock) {
      setIsEditing(false);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onUpdateStock(stock);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update stock');
      setStock(currentStock); // Reset to original value on error
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className={`font-medium ${currentStock < 10 ? 'text-red-500' : 'text-gray-900'}`}>
          {currentStock} units
        </span>
        <button
          onClick={() => setIsEditing(true)}
          className="text-sm text-brand-blue-500 hover:text-brand-blue-600"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleStockChange(stock - 1)}
          disabled={stock <= 0 || isSubmitting}
          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
        >
          <Minus className="w-4 h-4" />
        </button>

        <input
          type="number"
          value={stock}
          onChange={(e) => handleStockChange(parseInt(e.target.value) || 0)}
          className="w-20 px-2 py-1 border rounded text-center focus:ring-2 focus:ring-brand-blue-500"
          disabled={isSubmitting}
        />

        <button
          onClick={() => handleStockChange(stock + 1)}
          disabled={isSubmitting}
          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting || stock === currentStock}
          className="p-1 text-brand-blue-500 hover:text-brand-blue-600 disabled:opacity-50 
            disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {stock < 10 && (
        <p className="text-sm text-yellow-600">
          Warning: Low stock level
        </p>
      )}
    </div>
  );
};

export default StockEditor;